import { AxiosRequestConfig, AxiosResponse } from 'axios';
import { getValidatedBase64Cookie } from '@shared/cookies/getValidatedBase64Cookie';
import { getUtiqHost } from '@shared/getUtiqHost';
import {
  isNumber,
  isUtiqEligibility,
  isLogLevelType,
  isUtiqPass,
  makeValidator,
  Validator,
} from './utils';
import { readCookie } from '@shared/cookies/readCookie';
import { ConnectIdType } from '@idConnect/types';
import { EventNameType } from '@shared/config/types';
import { PayloadObject } from '@utiqLoader/types';

export type Didomi = {
  getCurrentUserStatus: () =>
    | null
    | undefined
    | {
        consent_string?: string;
        purposes: Partial<Record<string, { enabled?: boolean }>>;
        vendors: Partial<Record<string, { enabled?: boolean }>>;
      };
};

/**
  Ctx is the stateful information for the machine, it contains data that should be persisted between machine evaluations,
  and its values can be mutated directly
*/
export type Ctx = ReturnType<typeof makeCtx>;

export function makeCtx() {
  const utiqHost: string = getUtiqHost();

  const initialEligibilityData = JSON.parse(
    localStorage.getItem('utiqEligibility') ?? 'null',
  );

  window.Utiq ||= {};
  window.Utiq.initialEligibilityValue = initialEligibilityData?.isEligible;

  return {
    window,
    unbound: undefined as undefined,
    config: { utiqHost },
    Didomi: null as null | Didomi,
    isFirstRun: true,
    isIdConnect: false,

    /**
     * Stores previous values to detect updates and trigger effects
     * `undefined` means no value stored (first run), `null` means nullish value stored
     */
    journal: {} as Partial<Record<string, string | number | boolean | null>>,

    /**
     * Tracks times that things happen, used to check if an effect has already run
     */
    times: {} as Partial<Record<string, Date>>,

    /**
     * Stores analytics values to be sent via query string on mno-precheck calls
     * To send an analytics event, insert its into this Record:
     * - key: event name
     * - val: snap.now
     */
    analytics: {} as Partial<Record<string, Date>>,

    externalListenersRebroadcastEvent: {
      onConsentChanging: null,
      onConsentManagerStatusChanged: null,
      onConsentUpdateFinished: null,
      onEligibilityChecked: null,
      onIdsAvailable: null,
      onInitialised: null,
    } as Partial<Record<EventNameType, unknown | null>>,

    /**
     * Messages received from iframes
     * - evt: the raw browser event
     * - msg: the message object extracted from the event
     * - when: the Date timestamp when it was recieved
     */
    iframeMsgs: [] as Array<{
      evt: MessageEvent<unknown>;
      msg: unknown;
      when: Date;
    }>,

    /**
     * State properties for ID connection functionality
     */
    state: {
      idConnectUseCase: null as null | string,
      idConnectMnoSelectorDomain: null as null | string,
      resolvingConnectId: null as null | ConnectIdType,
    },

    /**
     * Axios-related properties for HTTP requests
     * - instance: a function used to trigger network requests
     * - active: the requests that have been made, but either
     *   - have not yet resolved a response
     *   - have a response which has not been handled by an effect yet
     * - log: a record of all requests that have been made, never remove an entry from this array.
     */
    axios: {
      instance: (_request: AxiosRequestConfig & { url: string }) => {},
      active: [] as Array<{
        req: AxiosRequestConfig;
        whenReq: Date;
        res?: AxiosResponse;
        whenRes?: Date;
      }>,
      log: [] as Array<{
        req: AxiosRequestConfig;
        whenReq: Date;
        res?: AxiosResponse;
        whenRes?: Date;
      }>,
    },
    consentPayload: null as PayloadObject | null,
  };
}

export type Snap = ReturnType<typeof makeSnap>;
export function makeSnap(ctx: Ctx) {
  const searchParams = new URLSearchParams(window.location.search);
  function getSearchParam<Key extends string, Val = string | null>(
    key: Key,
    validator?: Validator<Val>,
  ): Record<Key, Val> {
    const val = searchParams.get(key);
    if (validator) {
      return { [key]: validator(val) } as Record<Key, Val>;
    } else {
      return { [key]: val } as Record<Key, Val>;
    }
  }

  function getCookie<Key extends string, Val = string | null>(
    key: Key,
  ): Record<Key, Val> {
    const val = readCookie(document.cookie, key);
    return { [key]: val } as Record<Key, Val>;
  }

  function getBase64Cookie<Key extends string, Val = string | null>(
    key: Key,
    validator?: Validator<Val>,
  ): Record<Key, Val> {
    const val = getValidatedBase64Cookie(document.cookie, key);
    if (validator) {
      return { [key]: validator(val) } as Record<Key, Val>;
    } else {
      return { [key]: val } as Record<Key, Val>;
    }
  }

  function getSessionStorage<Key extends string, Val = string | null>(
    key: Key,
  ): Record<Key, string | Val> {
    const val = ctx.window.sessionStorage.getItem(key);
    return { [key]: val } as Record<Key, Val>;
  }

  function getLocalStorage<Key extends string, Val = string | null>(
    key: Key,
  ): Record<Key, string | Val> {
    const val = ctx.window.localStorage.getItem(key);
    return { [key]: val } as Record<Key, Val>;
  }

  function getLocalStorageJSON<Key extends string, Val = unknown>(
    key: Key,
    defaultValue: Val,
    validator: Validator<Val>,
  ): Record<Key, Val> {
    try {
      const val = JSON.parse(ctx.window.localStorage.getItem(key) ?? '');
      if (validator) {
        return { [key]: validator(val) } as Record<Key, Val>;
      } else {
        return { [key]: val } as Record<Key, Val>;
      }
    } catch {
      return { [key]: defaultValue } as Record<Key, Val>;
    }
  }

  function getDidomiConsents(
    Didomi: Didomi,
    didomiUtiqPurposeId: string | undefined,
    didomiUtiqVendorId: string | undefined,
  ) {
    const currentUserStatus = Didomi.getCurrentUserStatus();
    const didomiConsentExists = Boolean(currentUserStatus?.consent_string);
    /* We need to strip "c:" string from the config ID, as the newly used Didomi function also have it stripped in their data object */
    return {
      purposeConsent:
        didomiConsentExists && didomiUtiqPurposeId
          ? currentUserStatus?.purposes?.[
              didomiUtiqPurposeId?.replace('c:', '')
            ]?.enabled
          : undefined,
      vendorConsent:
        didomiConsentExists && didomiUtiqVendorId
          ? currentUserStatus?.vendors?.[didomiUtiqVendorId?.replace('c:', '')]
              ?.enabled
          : undefined,
    };
  }

  const queryString = {
    ...getSearchParam('utiq_stub'),
    ...getSearchParam('utiq_log_level', makeValidator(isLogLevelType, null)),
  };

  const cookies = {
    ...getCookie('OptanonConsent'),
    ...getBase64Cookie('utiq_consent_status'),
    ...getBase64Cookie('utiq_consent_version'),
    ...getBase64Cookie('utiq_consent_version_previous'),
    ...getBase64Cookie(
      'utiq_logging_level',
      makeValidator(isLogLevelType, null),
    ),
  };

  const localStorage = {
    ...getLocalStorage('_GESPSK-utiq.com'),
    ...getLocalStorage('utiqPass'),
    ...getLocalStorage('utiqStub'),
    ...getLocalStorage('utiq_consent_shown'),
    ...getLocalStorage('utiq_popup_ask_me_later'),
    ...getLocalStorage('utiq_openrtb_eids'),

    ...getLocalStorageJSON('utiqPass', null, makeValidator(isUtiqPass, null)),
    ...getLocalStorageJSON(
      'utiqDataCallTimestamp',
      null,
      makeValidator(isNumber, null),
    ),
    ...getLocalStorageJSON(
      'utiqEligibility',
      null,
      makeValidator(isUtiqEligibility, null),
    ),
  };

  const sessionStorage = {
    ...getSessionStorage('utiq_popup_ask_me_later'),
  };

  return {
    cookies,
    localStorage,
    queryString,
    sessionStorage,
    now: new Date(Date.now()),
    didomi:
      ctx.Didomi && ctx.window.Utiq
        ? getDidomiConsents(
            ctx.Didomi,
            ctx.window.Utiq?.config?.didomiUtiqPurposeId,
            ctx.window.Utiq?.config?.didomiUtiqVendorId,
          )
        : null,
  };
}
